import React, { useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchTestimonials } from '../../../redux/actions/Actions';
import { Container, Grid, IconButton, Stack, TextField } from '@mui/material';
import { Delete, Edit } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { DataGrid, GridToolbarContainer, GridToolbarExport } from '@mui/x-data-grid';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import securedInstance from '../../../../api/securedInstance';
import { toast } from 'react-toastify';

function CustomToolbar() {
  return (
    <GridToolbarContainer>
      <GridToolbarExport />
    </GridToolbarContainer>
  );
}

const Stars = ({ rating }) => (
  <span style={{ color: '#F59E0B', fontSize: 14 }}>
    {'★'.repeat(Math.max(0, Math.min(5, rating || 0)))}{'☆'.repeat(5 - Math.max(0, Math.min(5, rating || 0)))}
  </span>
);

const TestimonialList = () => {
  const testimonials = useSelector((s) => s.fetchAllTestimonials?.testimonials || []);
  const { user } = useSelector((s) => s.auth);
  const isAdmin = user?.role_id === 1;
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [data, setData]         = useState([]);
  const [dataRows, setDataRows] = useState([]);
  const [searchValue, setSearchValue] = useState('');

  useEffect(() => { dispatch(fetchTestimonials()); }, [dispatch]);

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this testimonial?')) return;
    try {
      await securedInstance.delete(`/api/testimonials/${id}`);
      toast.success('Testimonial deleted');
      dispatch(fetchTestimonials());
    } catch {
      toast.error('Delete failed');
    }
  };

  const columns = useMemo(() => [
    { field: 'testimonial_id', headerName: 'ID',   width: 60  },
    { field: 'candidate_name', headerName: 'Name', width: 160 },
    { field: 'job_role',     headerName: 'Role', width: 170 },
    {
      field: 'rating', headerName: 'Rating', width: 120,
      renderCell: (p) => <Stars rating={p.value} />,
    },
    {
      field: 'feedback', headerName: 'Feedback', width: 320,
      renderCell: (p) => (
        <div style={{ whiteSpace: 'normal', padding: '8px 0', fontSize: 13 }}>{p.value}</div>
      ),
    },
    ...(isAdmin ? [{
      field: 'action', headerName: 'Actions', width: 120, sortable: false,
      renderCell: (p) => (
        <Stack direction="row" spacing={1}>
          <IconButton size="small" onClick={() => navigate(`/auth/updateTestimonial/${p.row.testimonial_id}`)}>
            <Edit fontSize="small" />
          </IconButton>
          <IconButton size="small" onClick={() => handleDelete(p.row.testimonial_id)}>
            <Delete fontSize="small" />
          </IconButton>
        </Stack>
      ),
    }] : []),
  ], [isAdmin, navigate]);

  const rows = useMemo(() =>
    data.map((t, i) => ({ ...t, id: i + 1 })),
  [data]);

  const requestSearch = (val) => {
    setSearchValue(val);
    const q = val.toLowerCase();
    setDataRows(rows.filter((r) =>
      Object.values(r).some((v) => v != null && v.toString().toLowerCase().includes(q))
    ));
  };

  useEffect(() => {
    setData(testimonials);
  }, [testimonials]);

  useEffect(() => {
    setDataRows(rows);
  }, [rows]);

  return (
    <Container sx={{ width: '100vw', height: '100vh', overflow: 'auto' }}>
      <Grid container>
        <Grid item xs={6}>
          <TextField
            label="Search" variant="outlined" placeholder="Search…"
            sx={{ my: 2 }} value={searchValue}
            onChange={(e) => requestSearch(e.target.value)}
            InputProps={{
              startAdornment: <SearchIcon />,
              endAdornment: (
                <IconButton size="small"
                  style={{ visibility: searchValue ? 'visible' : 'hidden' }}
                  onClick={() => requestSearch('')}
                >
                  <ClearIcon fontSize="small" />
                </IconButton>
              ),
            }}
          />
        </Grid>
        <Grid item xs={12}>
          <DataGrid
            autoHeight rows={dataRows} columns={columns}
            getRowHeight={() => 'auto'}
            slots={{ toolbar: CustomToolbar }}
            sx={{
              '& .MuiDataGrid-columnHeaders': { fontSize: 15 },
              '&.MuiDataGrid-root--densityStandard .MuiDataGrid-cell': { py: '12px' },
            }}
            initialState={{ pagination: { paginationModel: { page: 0, pageSize: 10 } } }}
            pageSizeOptions={[10, 20, 50]}
          />
        </Grid>
      </Grid>
    </Container>
  );
};

export default TestimonialList;
